import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logementAPI, userAPI } from '../../service/api';
import { FiHome, FiPlus, FiMessageSquare, FiUser, FiMapPin, FiCamera, FiArrowRight } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import { toast } from '../../service/swal';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function OwnerDashboard() {
  const { user, loadUser } = useAuth();
  const [properties, setProperties] = useState([]);
  const avatarInputRef = useRef(null);

  const refreshData = useCallback(async () => {
    try {
      const r = await logementAPI.getMyProperties();
      setProperties(r.data.properties || []);
    } catch {}
  }, []);

  useEffect(() => { refreshData(); }, [refreshData]);
  useAutoRefresh(refreshData, 30000);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      await userAPI.uploadAvatar(formData);
      await loadUser();
      toast('success', 'Photo de profil mise à jour');
    } catch (err) {
      toast('error', 'Erreur lors du changement de photo');
    }
  };

  return (
    <div className="page-container py-8">
      <PageMeta title="Tableau de bord propriétaire" />
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="relative group cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
            {user?.avatarUrl ? (
              <img src={resolveUploadUrl(user.avatarUrl)} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 flex items-center justify-center text-xl font-bold rounded-full" style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
                {user?.nom?.charAt(0) || 'U'}
              </div>
            )}
            <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition">
              <FiCamera className="w-5 h-5 text-white" />
            </div>
            <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={handleAvatarChange} />
          </div>
          <div>
            <h1 className="font-serif text-2xl font-bold tracking-tight" style={{ color: '#0A192F' }}>Bonjour, {user?.nom}</h1>
            <p className="text-sm" style={{ color: '#7A828A' }}>Propriétaire</p>
          </div>
        </div>
        <Link to="/logements/nouveau" className="btn-primary-root inline-flex items-center gap-2">
          <FiPlus /> Publier une annonce <FiArrowRight />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="card-root p-6">
            <h2 className="font-serif font-bold text-base mb-4" style={{ color: '#0A192F' }}>
              Mes logements ({properties.length})
            </h2>
            {properties.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: '#7A828A' }}>Vous n'avez pas encore publié d'annonce</p>
            ) : (
              <div className="space-y-4">
                {properties.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-4" style={{ border: '2px solid #E5E5E0' }}>
                    <div>
                      <h3 className="font-semibold" style={{ color: '#0A192F' }}>{p.titre}</h3>
                      <p className="text-xs flex items-center gap-1 mt-1" style={{ color: '#7A828A' }}>
                        <FiMapPin /> {p.adresse}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold" style={{ color: '#C59B6C' }}>{Number(p.prix).toLocaleString()} $</p>
                      <span className={`text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2 ${
                        p.statut === 'disponible' ? 'border-green-600 text-green-600' :
                        p.statut === 'reserve' ? 'border-yellow-600 text-yellow-600' : 'border-red-600 text-red-600'
                      }`}>
                        {p.statut}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="card-root p-6 mb-6">
            <h2 className="font-serif font-bold text-base mb-4" style={{ color: '#0A192F' }}>Statistiques</h2>
            <div className="space-y-4">
              {[
                { label: 'TOTAL ANNONCES', value: properties.length, color: '#0A192F' },
                { label: 'DISPONIBLES', value: properties.filter(p => p.statut === 'disponible').length, color: '#2E7D32' },
                { label: 'LOUÉS', value: properties.filter(p => p.statut === 'loue').length, color: '#C62828' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex justify-between items-center py-2" style={{ borderBottom: '1px solid #E5E5E0' }}>
                  <span className="label-root">{label}</span>
                  <span className="font-bold" style={{ color }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
          <Link to="/messages" className="card-root p-5 flex items-center gap-3 hover:shadow-md transition-shadow">
            <FiMessageSquare className="w-6 h-6" style={{ color: '#C59B6C' }} />
            <div className="flex-1">
              <p className="font-bold text-sm" style={{ color: '#0A192F' }}>Messagerie</p>
              <p className="label-root">Voir mes messages</p>
            </div>
            <FiArrowRight className="w-4 h-4" style={{ color: '#C59B6C' }} />
          </Link>
        </div>
      </div>
    </div>
  );
}
